import { ShopOnboardingID } from '@prisma/client';
import { Typography, Box, Button } from '@mui/material';
import { toast } from 'react-toastify';
import PageLayout from '../components/global/PageLayout';
import useUser from '../hooks/global/useUser';
import { isUserAdminOrManager } from '../utils/global/helper';
import OnboardingBanner from '../components/onboarding/OnboardingBanner';
import CenteredContentLayout from '../components/global/CenteredContentLayout';
import TextStep from '../components/global/TextStep';

export default function ImportPage() {
  const { user } = useUser();

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/data_import.xlsx';
    link.download = 'porter-import-template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Template downloaded successfully');
  };

  return (
    <PageLayout
      topSection={<OnboardingBanner step={ShopOnboardingID.IMPORT_DATA} />}
      title="Import your data"
      guard={{
        isAllowed:
          !!user &&
          isUserAdminOrManager(user) &&
          user?.business.type === 'STUDIO',
      }}>
      <CenteredContentLayout
        image={{
          src: '/rotation.svg',
          alt: 'Import your data',
        }}
        title="Let's get started with your data import"
        description="You can either export your data directly from your current system, or manually enter it into a spreadsheet. Once you have your data ready, email it to support@getporter.io and we'll take care of the rest!"
        imageMaxWidth="120px"
        maxWidth="900px">
        <Box width="100%" display="flex" flexDirection="column" gap={2}>
          <Typography variant="h2">
            Option 1: Export directly from your system
          </Typography>

          <TextStep
            title="Step 1: Request an export of all of your data"
            description="We accept exports from all systems, including Daysmart, Square, Rev23, GlossGenius, and many others. If you don't see your system listed, just ask us."
          />
          <TextStep
            title="Step 2: Email your export file"
            description="When your export is ready, email it to support@getporter.io"
          />
        </Box>

        <Box width="100%" display="flex" flexDirection="column" gap={2}>
          <Typography variant="h2">
            Option 2: Manually enter your data into our import template
          </Typography>

          <TextStep
            title="Step 1: Download the import template"
            description="Download the import template"
          />
          <TextStep
            title="Step 2: Fill in the template with your data"
            description="Fill in the template with your data"
          />
          <TextStep
            title="Step 3: Email the completed template"
            description="Email the completed template to support@getporter.io"
          />

          <Button
            variant="light"
            sx={{
              whiteSpace: 'nowrap',
              backgroundColor: '#ffffff',
              width: 'fit-content',
              px: 4,
              py: 1.5,
              borderRadius: 2, // Slightly rounded corners
              boxShadow: '0px 2px 4px rgba(0,0,0,0.05)', // Subtle shadow
              '&:hover': {
                backgroundColor: '#f8f9fa', // Subtle hover effect
              },
            }}
            onClick={handleDownloadTemplate}>
            Download the import template
          </Button>
        </Box>
      </CenteredContentLayout>
    </PageLayout>
  );
}
